/**
 * Pages/Panel/ReportesNomina/ReporteComparativoPeriodos.js
 *
 * Comparativo del neto de cada empleado entre varias prenóminas.
 *
 * Un renglón por empleado y una columna por periodo, más la variación contra el
 * periodo inmediato anterior. Sirve para cachar errores ANTES de timbrar: un neto
 * que se dispara o se desploma casi siempre es una incidencia mal capturada, un
 * concepto manual que se quedó pegado o una baja que no se aplicó.
 *
 * El backend no baja ni una línea de prenómina: los totales salen de un $group y
 * sólo se hidratan los empleados que aparecieron.
 */

import { InputNumber, message, PageHeader, Select, Space, Spin, Table, Tabs, Tag } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import {
  headerStyle,
  textoStyle,
  centroStyle,
  monedaStyle,
  totalTextoStyle,
  totalMonedaStyle,
  celda,
  celdaMoneda,
} from "./estilosExcelNomina";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// Variación por debajo de la cual no vale la pena mirar el renglón. Es un default:
// el usuario lo mueve en la pantalla.
const UMBRAL_DEFAULT = 20;

class ReporteComparativoPeriodos extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    // Prenóminas del rango, para elegir cuáles comparar
    prenominas: [],
    seleccionadas: [],
    // Resultado del comparativo
    columnas: [],
    filas: [],
    umbral: UMBRAL_DEFAULT,
    objetoRegistrosPatronales: null,
    // Un finiquito se paga dentro de la prenómina ordinaria, así que el periodo en el que cae
    // una liquidación se dispara y arruina la lectura de la tendencia. 'todos' | 'sin' | 'solo'.
    filtroFiniquito: "todos",
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: (res.data.registrosPatronales || []).map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarPrenominas(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];

    axios
      .post(
        "reportesNomina/prenominasParaReporte",
        {
          empresaId: this.props.empresaId,
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          tipoPeriodoId: null,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          const prenominas = res.data.prenominas || [];
          this.setState({
            loading: false,
            consultado: true,
            prenominas,
            // Se preseleccionan todas: comparar el rango completo es el caso normal
            seleccionadas: prenominas.filter((p) => p.empleados > 0).map((p) => p._id),
            columnas: [],
            filas: [],
          });
          if (prenominas.length === 0) {
            message.info("No hay prenóminas con fecha de pago en ese rango");
          }
        } else if (res.data.success === 1) {
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargaron las prenóminas");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargaron las prenóminas");
        this.setState({ loading: false });
      });
  }

  compararSeleccionadas() {
    const { seleccionadas } = this.state;
    if (seleccionadas.length < 2) {
      return message.warning("Selecciona al menos dos prenóminas para comparar");
    }

    const valores = this.formRef.current ? this.formRef.current.getFieldsValue() : {};
    this.setState({ loading: true });

    axios
      .post(
        "reportesNomina/reporteComparativoPeriodos",
        {
          empresaId: this.props.empresaId,
          prenominaIds: seleccionadas,
          registrosPatronales: valores.registrosPatronales || [],
          departamentos: valores.departamentos || [],
          filtroFiniquito: this.state.filtroFiniquito,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            empresa: res.data.empresa,
            columnas: res.data.columnas || [],
            filas: res.data.filas || [],
          });
        } else if (res.data.success === 1) {
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se generó el comparativo");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se generó el comparativo");
        this.setState({ loading: false });
      });
  }

  // ── Cálculo compartido por la tabla en pantalla y el Excel ────────────────
  /**
   * Netos de un empleado en el orden de las columnas, con la variación porcentual
   * contra el periodo anterior en el que SÍ tuvo recibo.
   *
   * Un periodo sin recibo no vale 0: vale "no estuvo", que es distinto — un
   * empleado que entró a media quincena no tiene una caída del 100%.
   */
  netosDeFila(fila) {
    const { columnas } = this.state;
    let previo = null;
    return columnas.map((col) => {
      const dato = fila.periodos ? fila.periodos[col._id] : null;
      if (!dato) return { neto: null, variacion: null };
      const neto = dato.neto || 0;
      const variacion = previo !== null && previo !== 0
        ? ((neto - previo) / Math.abs(previo)) * 100
        : null;
      previo = neto;
      return { neto, variacion };
    });
  }

  descargarExcel() {
    const { columnas, filas, empresa, umbral } = this.state;
    if (filas.length === 0) return;

    const aoa = [];

    aoa.push([celda(empresa?.nombre || "", { font: { bold: true, sz: 14 } })]);
    aoa.push([celda("Comparativo de nómina entre periodos", { font: { sz: 11 } })]);
    aoa.push([celda(`Variación resaltada a partir de ${umbral}%`, { font: { sz: 9, italic: true } })]);
    aoa.push([]);

    // Encabezado: dos columnas por periodo (neto y variación)
    const encabezado = [
      celda("Código", headerStyle),
      celda("Empleado", headerStyle),
      celda("Departamento", headerStyle),
      celda("Reg. Pat.", headerStyle),
    ];
    columnas.forEach((col) => {
      encabezado.push(celda(col.nombre, headerStyle))
      encabezado.push(celda("Var. %", headerStyle))
    });
    aoa.push(encabezado);

    const totales = columnas.map(() => 0);

    filas.forEach((fila) => {
      const netos = this.netosDeFila(fila);
      const renglon = [
        celda(fila.codigo == null ? "" : String(fila.codigo).padStart(4, "0"), textoStyle),
        celda(fila.nombre, textoStyle),
        celda(fila.departamento, textoStyle),
        celda(fila.registroPatronalIMSS, centroStyle),
      ];
      netos.forEach((valor, i) => {
        renglon.push(celdaMoneda(valor.neto === null ? "" : valor.neto, monedaStyle));
        // La variación se escribe como número para poder ordenarla en Excel; el
        // formato le pone el signo de porcentaje.
        renglon.push(
          valor.variacion === null
            ? celda("", centroStyle)
            : {
                v: Number(valor.variacion.toFixed(2)),
                t: "n",
                s: {
                  ...centroStyle,
                  numFmt: '0.00"%"',
                  font: Math.abs(valor.variacion) >= umbral
                    ? { bold: true, color: { rgb: valor.variacion < 0 ? "C00000" : "1F7A1F" } }
                    : undefined,
                },
              }
        );
        if (valor.neto !== null) totales[i] += valor.neto;
      });
      aoa.push(renglon);
    });

    const filaTotal = [
      celda("TOTAL", totalTextoStyle),
      celda("", totalTextoStyle),
      celda("", totalTextoStyle),
      celda("", totalTextoStyle),
    ];
    totales.forEach((total) => {
      filaTotal.push(celdaMoneda(total, totalMonedaStyle));
      filaTotal.push(celda("", totalTextoStyle));
    });
    aoa.push(filaTotal);

    const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
    hoja["!cols"] = [
      { wch: 10 }, { wch: 34 }, { wch: 22 }, { wch: 16 },
      ...columnas.flatMap(() => [{ wch: 15 }, { wch: 10 }]),
    ];

    const libro = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(libro, hoja, "Comparativo");
    XLSXStyle.writeFile(
      libro,
      `Comparativo_Nomina_${moment().format("YYYY_MM_DD")}.xlsx`
    );
  }

  render() {
    const {
      loading, consultado, prenominas, seleccionadas, columnas, filas, umbral, filtroFiniquito,
    } = this.state;

    // Los netos de cada empleado se calculan UNA vez por render, no dentro del
    // render de cada celda: si no, con 12 periodos cada renglón recorre las columnas
    // doce veces para pintar doce celdas.
    const netosPorEmpleado = new Map(
      filas.map((fila) => [fila.empleadoId, this.netosDeFila(fila)])
    );

    const columnasPrenominas = [
      { title: "Periodo", dataIndex: "periodoNumero", width: 90, render: (v) => (v == null ? "—" : v) },
      { title: "Nombre", dataIndex: "nombre" },
      { title: "Tipo", dataIndex: "tipoPeriodoNombre", width: 120 },
      {
        title: "Fecha de pago",
        dataIndex: "fechaPago",
        width: 120,
        align: "center",
        render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—"),
      },
      { title: "Empleados", dataIndex: "empleados", width: 100, align: "right" },
    ];

    // Un par de columnas (neto + variación) por periodo comparado
    const columnasComparativo = [
      {
        title: "Código",
        dataIndex: "codigo",
        width: 90,
        fixed: "left",
        render: (v) => (v == null ? "—" : String(v).padStart(4, "0")),
      },
      { title: "Empleado", dataIndex: "nombre", width: 240, fixed: "left" },
      { title: "Departamento", dataIndex: "departamento", width: 160 },
      ...columnas.map((col, i) => ({
        title: col.nombre,
        key: col._id,
        width: 160,
        align: "right",
        render: (_, fila) => {
          const valor = (netosPorEmpleado.get(fila.empleadoId) || [])[i];
          if (!valor || valor.neto === null) return <span style={{ color: "#bbb" }}>—</span>;
          const alerta = valor.variacion !== null && Math.abs(valor.variacion) >= umbral;
          return (
            <span>
              {dineroDisabledMask(valor.neto)}
              {valor.variacion !== null && (
                <Tag
                  color={alerta ? (valor.variacion < 0 ? "red" : "green") : "default"}
                  style={{ marginLeft: 6 }}
                >
                  {valor.variacion > 0 ? "+" : ""}
                  {valor.variacion.toFixed(1)}%
                </Tag>
              )}
            </span>
          );
        },
      })),
    ];

    const conAlerta = filas.filter((fila) => {
      const variaciones = (netosPorEmpleado.get(fila.empleadoId) || [])
        .map((v) => v.variacion)
        .filter((v) => v !== null && Number.isFinite(v));
      return variaciones.some((v) => Math.abs(v) >= umbral);
    });

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteComparativoPeriodos"
          onFinish={this.consultarPrenominas.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Comparativo entre Periodos"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {filas.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Fecha de pago">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <Space wrap style={{ marginBottom: 12 }}>
                  <span style={{ color: "#666" }}>
                    Periodos a comparar ({seleccionadas.length} de {prenominas.length}):
                  </span>
                  <ButtonItem
                    type="primary"
                    disabled={seleccionadas.length < 2}
                    onClick={() => this.compararSeleccionadas()}
                  >
                    Comparar
                  </ButtonItem>
                  <span style={{ color: "#666", marginLeft: 12 }}>Resaltar variación mayor a</span>
                  <InputNumber
                    min={1}
                    max={500}
                    value={umbral}
                    onChange={(v) => this.setState({ umbral: v || UMBRAL_DEFAULT })}
                    formatter={(v) => `${v}%`}
                    parser={(v) => String(v).replace("%", "")}
                    style={{ width: 90 }}
                  />
                  {/* El periodo en el que cae una liquidación se dispara: un finiquito puede
                      valer varias veces el sueldo mensual y se lee como una variación enorme
                      que no lo es. Se puede sacar de la comparación, o mirar sólo ésos. */}
                  <span style={{ color: "#666", marginLeft: 12 }}>Finiquitos</span>
                  <Select
                    value={filtroFiniquito}
                    onChange={(v) => this.setState({ filtroFiniquito: v })}
                    style={{ width: 170 }}
                  >
                    <Option value="sin">
                      <span style={{ fontWeight: 600, color: "#00BFD6" }}>Sólo la ordinaria</span>
                    </Option>
                    <Option value="todos">Incluir finiquitos</Option>
                    <Option value="solo">Sólo finiquitos</Option>
                  </Select>
                </Space>

                <Table
                  size="small"
                  rowKey="_id"
                  dataSource={prenominas}
                  columns={columnasPrenominas}
                  pagination={{ defaultPageSize: 8 }}
                  rowSelection={{
                    selectedRowKeys: seleccionadas,
                    onChange: (keys) => this.setState({ seleccionadas: keys }),
                    getCheckboxProps: (record) => ({ disabled: record.empleados === 0 }),
                  }}
                />
              </div>
            )}

            {filas.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {filas.length} empleado(s) ·{" "}
                  <b style={{ color: conAlerta.length > 0 ? "#cf1322" : "#389e0d" }}>
                    {conAlerta.length}
                  </b>{" "}
                  con variación de al menos {umbral}% entre periodos consecutivos.
                </p>
                <Table
                  size="small"
                  rowKey="empleadoId"
                  dataSource={filas}
                  columns={columnasComparativo}
                  pagination={{ defaultPageSize: 20 }}
                  scroll={{ x: 700 + columnas.length * 160 }}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteComparativoPeriodos);
